import { createContext, useState } from "react";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

import {
  createUserEmployerService,
  createUserService,
  deleteUserEmployerService,
  deleteUserService,
  editUserService,
  getEmployersService,
  getUserAddressesService,
  getUserService,
  getUsersService,
} from "./../../../services/userServices";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "./../../../redux/actions/loadingActions/loadingActions";
import { isValidPhoneNumber } from "./../../../utils/index";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";

export const userAdminContext = createContext({
  employers: [],
  setEmployers: () => {},
  users: [],
  setUsers: () => {},
  addresses: [],
  setAddresses: () => {},
  user: {},
  setUser: () => {},
  employer: "",
  setEmployer: () => {},
  app_type: "",
  setApp_type: () => {},
  app_version: "",
  setApp_version: () => {},
  sort_by: "",
  setSort_by: () => {},
  search: "",
  setSearch: () => {},
  id: "",
  setId: () => {},
  first_name: "",
  setFirst_name: () => {},
  last_name: "",
  setLast_name: () => {},
  mobile: "",
  setMobile: () => {},
  email: "",
  setEmail: () => {},
  introduction: "",
  setIntroduction: () => {},
  totalCount: null,
  setTotalCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  limit: null,
  setLimit: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  isShowDetailsModal: null,
  setIsShowDetailsModal: () => {},
  isShowAdvancedSearch: null,
  setIsShowAdvancedSearch: () => {},
  handleShowDetailsModal: () => {},
  handleCloseDetailsModal: () => {},
  handleGetEmployers: () => {},
  handleCreateUser: () => {},
  handleEditUser: () => {},
  handleShowModal: () => {},
  handleCloseModal: () => {},
  handleGetUsers: () => {},
  handleGetUser: () => {},
  handleDeleteUser: () => {},
  handleCreateUserEmployer: () => {},
  handleDeleteUserEmployer: () => {},
  handleGetAddresses: () => {},
});

const UserAdminContext = ({ setForcePhoneNumber, children }) => {
  // Redux Dispatch
  const dispatch = useDispatch();

  const [users, setUsers] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [user, setUser] = useState({});
  const [employer, setEmployer] = useState("");
  const [search, setSearch] = useState("");
  const [id, setId] = useState("");
  const [sort_by, setSort_by] = useState("");
  const [app_type, setApp_type] = useState("");
  const [app_version, setApp_version] = useState("");
  const [first_name, setFirst_name] = useState("");
  const [last_name, setLast_name] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [introduction, setIntroduction] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [limit, setLimit] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);
  const [isShowDetailsModal, setIsShowDetailsModal] = useState(false);
  const [isShowAdvancedSearch, setIsShowAdvancedSearch] = useState(false);

  const handleResetState = () => {
    setId("");
    setApp_type("");
    setSort_by("");
    setApp_version("");
    setFirst_name("");
    setLast_name("");
    setMobile("");
    setEmail("");
    setIntroduction("");
    setUser({});
    setAddresses([]);
  };

  // Create User Modal Operations
  const handleShowModal = (value) => {
    setIsShowModal(true);
    if (value) {
      setId(value?.pk);
      setFirst_name(value?.first_name);
      setLast_name(value?.last_name);
      setMobile(value?.mobile);
      setEmail(value?.email);
      setIntroduction(value?.introduction);
      setUser(value);
      handleGetAddresses(value);
    }
  };
  const handleCloseModal = () => {
    setIsShowModal(false);
    handleResetState();
  };

  // Details User Modal Operations
  const handleShowDetailsModal = (value) => {
    setIsShowDetailsModal(true);
    setUser(value);
  };
  const handleCloseDetailsModal = () => {
    setIsShowDetailsModal(false);
    setUser({});
  };

  // Handle Get Employers Method
  const handleGetEmployers = async () => {
    try {
      const { status, data } = await getEmployersService();
      if (status === 200) {
        setEmployers(data.list);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    }
  };

  // Handle Get Users Method
  const handleGetUsers = async (event, _forceSearch) => {
    event?.preventDefault();
    const isSearch = !isEmpty(event);

    const objData = {
      ...(!_forceSearch && employer && { employer }),
      ...(!_forceSearch && search && { search }),
      ...(app_type && { app_type }),
      ...(app_version && { app_version }),
      ...(sort_by && { sort_by }),
      offset: ((isSearch ? 1 : pageNumber) - 1) * limit,
      limit,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUsersService(objData);
      if (status === 200) {
        setUsers(data.results);
        setTotalCount(data.count);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Get User Method
  const handleGetUser = async (pk, func) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUserService(pk);
      if (status === 200) {
        if (func) func(data);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Create User Method
  const handleCreateUser = async (event) => {
    event?.preventDefault();

    const organizationCode = event?.target?.organizationCode?.value;
    const employeeCode = event?.target?.employeeCode?.value;

    if (!isValidPhoneNumber(mobile))
      return toastErrorMessage("شماره موبایل معتبر نمیباشد");

    const objData = {
      items: {
        first_name,
        last_name,
        mobile,
        email,
        ...(introduction && {
          introduction: +introduction,
        }),
        ...(organizationCode && {
          employer_code: organizationCode,
        }),
        ...(employeeCode && {
          employee_code: employeeCode,
        }),
      },
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createUserService(objData);
      if (status === 200 || status === 201) {
        toastSuccessMessage(
          `کاربر ${first_name} ${last_name} با موفقیت ایجاد شد`
        );
        handleCloseModal();
        setSearch(mobile);
        handleGetUsers(null, mobile);
        if (setForcePhoneNumber) setForcePhoneNumber(mobile);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage(response?.data?.error);
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit User Method
  const handleEditUser = async (event) => {
    event?.preventDefault();

    if (!isValidPhoneNumber(mobile))
      return toastErrorMessage("شماره موبایل معتبر نمیباشد");

    const objData = {
      pk: id,
      first_name,
      last_name,
      mobile,
      email,
      ...(introduction && {
        introduction: +introduction,
      }),
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editUserService(objData);
      if (status === 200 || status === 201) {
        toastSuccessMessage(`کاربر ${id} با موفقیت ویرایش شد`);
        handleCloseModal();
        handleGetUsers();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage(response?.data?.error);
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete User Method
  const handleDeleteUser = async (pk) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteUserService(pk);
      if (status === 200) {
        toastSuccessMessage(`کاربر  ${pk} با موفقیت حذف شد`);
        handleGetUsers();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Create User Employer Method
  const handleCreateUserEmployer = async (event) => {
    event?.preventDefault();

    const organizationCode = event?.target?.organizationCode?.value;
    const employeeCode = event?.target?.employeeCode?.value;

    if (!organizationCode || !employeeCode)
      return toastErrorMessage("اطلاعات وارد شده نادرست");
    const objData = {
      user_id: user.pk,
      employer_code: organizationCode,
      employee_code: employeeCode,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createUserEmployerService(objData);
      if (status === 200 || status === 201) {
        handleGetUser(user.pk, handleShowModal);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage(response?.data?.message);
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete User Employer Method
  const handleDeleteUserEmployer = async () => {
    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteUserEmployerService({ user_id: user.pk });
      if (status === 200) {
        handleGetUser(user.pk, handleShowModal);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Get Addresses Method
  const handleGetAddresses = async (userData) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUserAddressesService({
        user_id: userData.pk,
      });
      if (status === 200) {
        setAddresses(data.list);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage(response?.data?.error);
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <userAdminContext.Provider
      value={{
        employers,
        setEmployers,
        users,
        setUsers,
        addresses,
        setAddresses,
        user,
        setUser,
        employer,
        setEmployer,
        search,
        setSearch,
        id,
        setId,
        sort_by,
        setSort_by,
        app_type,
        setApp_type,
        app_version,
        setApp_version,
        first_name,
        setFirst_name,
        last_name,
        setLast_name,
        mobile,
        setMobile,
        email,
        setEmail,
        introduction,
        setIntroduction,
        totalCount,
        setTotalCount,
        pageNumber,
        setPageNumber,
        limit,
        setLimit,
        isShowModal,
        setIsShowModal,
        isShowDetailsModal,
        setIsShowDetailsModal,
        isShowAdvancedSearch,
        setIsShowAdvancedSearch,
        handleShowDetailsModal,
        handleCloseDetailsModal,
        handleGetEmployers,
        handleCreateUser,
        handleEditUser,
        handleShowModal,
        handleCloseModal,
        handleGetUsers,
        handleGetUser,
        handleDeleteUser,
        handleCreateUserEmployer,
        handleDeleteUserEmployer,
        handleGetAddresses,
      }}
    >
      {children}
    </userAdminContext.Provider>
  );
};

export default UserAdminContext;
